import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import {
  enrichVersionsWithStorageUrls,
  uploadResourceFile,
} from './storage';

const PLACEHOLDER_THUMB =
  'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';

const AUDIENCE_LABELS = {
  cliente: 'Cliente',
  interno: 'Interno',
};

const FILE_FORMAT_LABELS = {
  pdf: 'PDF',
  docx: 'Word',
  md: 'Markdown',
};

const LINK_KIND_LABELS = {
  video: 'Video',
  canva: 'Canva',
  google_slides: 'Google Slides',
  google_sheets: 'Google Sheets',
  google_docs: 'Google Docs',
  other: 'Enlace externo',
};

const RESOURCE_SELECT = `
  id,
  title,
  description,
  category,
  audience,
  uploaded_by,
  created_at,
  updated_at,
  uploader:gfi_profiles!gfi_resources_uploaded_by_fkey (
    id,
    full_name
  )
`;

const VERSION_FIELDS = `
  id,
  version_number,
  version_label,
  content_type,
  publitio_file_id,
  storage_path,
  file_url,
  preview_url,
  file_format,
  external_url,
  link_kind,
  thumbnail_url,
  note,
  is_current,
  uploaded_at
`;

function mapProfile(profile) {
  if (!profile) return null;
  return Array.isArray(profile) ? profile[0] : profile;
}

function formatResourceDate(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "d MMM yyyy", { locale: es });
  } catch {
    return '—';
  }
}

export function inferLinkKind(url) {
  if (!url) return 'other';
  const lower = url.toLowerCase();
  if (lower.includes('canva.com')) return 'canva';
  if (lower.includes('docs.google.com')) return 'google_docs';
  if (lower.includes('sheets.google.com')) return 'google_sheets';
  if (lower.includes('slides.google.com')) return 'google_slides';
  if (
    lower.includes('youtube.com')
    || lower.includes('youtu.be')
    || lower.includes('vimeo.com')
  ) {
    return 'video';
  }
  return 'other';
}

export function getLinkSourceLabel(linkKind, externalUrl) {
  const kind = linkKind && linkKind !== 'other' ? linkKind : inferLinkKind(externalUrl);
  return LINK_KIND_LABELS[kind] ?? LINK_KIND_LABELS.other;
}

/** Publit.io /download/ URLs force attachment; /file/ URLs serve inline for PDF preview. */
export function getPublitioFilePreviewUrl(fileUrl, storedPreviewUrl = null) {
  if (storedPreviewUrl?.trim()) return storedPreviewUrl.trim();

  if (!fileUrl) return null;

  try {
    const url = new URL(fileUrl);
    if (url.hostname.includes('publit.io') && url.pathname.startsWith('/download/')) {
      return `${url.protocol}//${url.hostname}/file${url.pathname.slice('/download'.length)}`;
    }
  } catch {
    // fall through
  }

  return fileUrl;
}

function mapVersionRow(version) {
  if (!version) return null;

  const isFile = version.content_type === 'file';
  const linkKind = version.link_kind || inferLinkKind(version.external_url);

  return {
    id: version.id,
    type: version.content_type,
    version: version.version_label,
    versionNumber: version.version_number,
    format: isFile ? (FILE_FORMAT_LABELS[version.file_format] ?? version.file_format) : null,
    source: isFile ? null : getLinkSourceLabel(linkKind, version.external_url),
    link: isFile ? version.file_url : version.external_url,
    previewLink: isFile
      ? getPublitioFilePreviewUrl(version.file_url, version.preview_url)
      : null,
    thumbnail: version.thumbnail_url || PLACEHOLDER_THUMB,
    note: version.note || '',
    date: formatResourceDate(version.uploaded_at),
    uploadedAt: version.uploaded_at,
    isCurrent: version.is_current,
    publitioFileId: version.publitio_file_id,
    storagePath: version.storage_path,
    isLegacyPublitioFile: Boolean(version.publitio_file_id && !version.storage_path),
  };
}

export function mapResourceRow(row, { versions = null } = {}) {
  const uploader = mapProfile(row.uploader);
  const versionRows = versions ?? row.versions ?? [];
  const sortedVersions = [...versionRows].sort((a, b) => b.version_number - a.version_number);
  const current = sortedVersions.find((v) => v.is_current) ?? sortedVersions[0];
  const currentMapped = mapVersionRow(current);

  if (!currentMapped) return null;

  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    category: row.category,
    audience: AUDIENCE_LABELS[row.audience] ?? row.audience,
    audienceValue: row.audience,
    uploader: uploader?.full_name || 'GFI Latino',
    uploadedBy: row.uploaded_by,
    date: currentMapped.date,
    version: currentMapped.version,
    isUpdated: sortedVersions.length > 1,
    thumbnail: currentMapped.thumbnail,
    type: currentMapped.type,
    format: currentMapped.format,
    fileFormat: current?.file_format ?? null,
    source: currentMapped.source,
    link: currentMapped.link,
    previewLink: currentMapped.previewLink,
    versions: sortedVersions.map((v) => mapVersionHistoryEntry(v)),
  };
}

function mapVersionHistoryEntry(version) {
  return {
    id: version.id,
    v: version.version_label,
    date: formatResourceDate(version.uploaded_at),
    note: version.note || 'Sin notas',
    isCurrent: version.is_current,
  };
}

export function applyVersionToResource(resource, versionRow) {
  const mapped = mapVersionRow(versionRow);
  if (!mapped) return resource;

  return {
    ...resource,
    date: mapped.date,
    version: mapped.version,
    thumbnail: mapped.thumbnail,
    type: mapped.type,
    format: mapped.format,
    fileFormat: versionRow.file_format ?? null,
    source: mapped.source,
    link: mapped.link,
    previewLink: mapped.previewLink,
    viewingVersionId: mapped.id,
    viewingVersionLabel: mapped.version,
  };
}

export async function fetchResourceVersionHistory(resourceId) {
  const { data, error } = await supabase.rpc('gfi_list_resource_version_history', {
    p_resource_id: resourceId,
  });
  if (error) throw error;

  return (data ?? []).map((row) => mapVersionHistoryEntry(row));
}

export async function fetchResourceVersion(resourceId, versionId) {
  const { data, error } = await supabase
    .from('gfi_resource_versions')
    .select(VERSION_FIELDS)
    .eq('resource_id', resourceId)
    .eq('id', versionId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const [enriched] = await enrichVersionsWithStorageUrls([data]);
  return enriched;
}

export async function checkCanUploadResources() {
  const { data, error } = await supabase.rpc('gfi_can_upload_resources');
  if (error) throw error;
  return Boolean(data);
}

export async function requestResourceVersionAccess(resourceId, versionId) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No autenticado');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resource-version-access-request`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ resourceId, versionId }),
    },
  );

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'No se pudo enviar la solicitud');
  }
  return json;
}

export async function fetchResources({ category, audience, search, limit = 100 } = {}) {
  let query = supabase
    .from('gfi_resources')
    .select(`
      ${RESOURCE_SELECT},
      versions:gfi_resource_versions!inner (${VERSION_FIELDS})
    `)
    .eq('versions.is_current', true)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (category) query = query.eq('category', category);
  if (audience) query = query.eq('audience', audience);
  if (search?.trim()) query = query.ilike('title', `%${search.trim()}%`);

  const { data, error } = await query;
  if (error) throw error;

  const enrichedRows = await Promise.all(
    (data ?? []).map(async (row) => {
      const versions = await enrichVersionsWithStorageUrls(row.versions);
      return mapResourceRow({ ...row, versions });
    }),
  );

  return enrichedRows.filter(Boolean);
}

export async function fetchResourceById(id) {
  const { data, error } = await supabase
    .from('gfi_resources')
    .select(`
      ${RESOURCE_SELECT},
      versions:gfi_resource_versions (${VERSION_FIELDS})
    `)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data?.versions?.length) return null;

  const versions = await enrichVersionsWithStorageUrls(data.versions);
  return mapResourceRow({ ...data, versions });
}

export async function fetchDistinctResourceCategories() {
  const { data, error } = await supabase.from('gfi_resources').select('category');
  if (error) throw error;

  const set = new Set();
  (data ?? []).forEach((row) => {
    if (row.category?.trim()) set.add(row.category.trim());
  });
  return [...set].sort((a, b) => a.localeCompare(b, 'es'));
}

export function detectFileFormat(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.docx')) return 'docx';
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'md';

  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
  if (file.type === 'text/markdown') return 'md';

  return null;
}

export async function checkCanVersionResource(originalUploaderId) {
  const { data, error } = await supabase.rpc('gfi_can_version_resource', {
    p_original_uploader: originalUploaderId,
  });
  if (error) throw error;
  return Boolean(data);
}

function buildVersionPayload(content, { uploadedBy, note, versionLabel, isCurrent = true }) {
  const base = {
    content_type: content.type,
    note: note?.trim() ?? '',
    is_current: isCurrent,
    uploaded_by: uploadedBy,
  };

  if (versionLabel?.trim()) {
    base.version_label = versionLabel.trim();
  }

  if (content.type === 'file') {
    return {
      ...base,
      id: content.versionId,
      storage_path: content.storagePath,
      publitio_file_id: null,
      file_url: null,
      preview_url: null,
      file_format: content.fileFormat,
      thumbnail_url: null,
      external_url: null,
      link_kind: null,
    };
  }

  return {
    ...base,
    publitio_file_id: null,
    file_url: null,
    file_format: null,
    thumbnail_url: null,
    external_url: content.externalUrl.trim(),
    link_kind: content.linkKind,
  };
}

export async function publishResource({
  title,
  description,
  category,
  audience,
  complianceConfirmed,
  content,
  versionNote,
  versionLabel,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!complianceConfirmed) throw new Error('Debes confirmar el cumplimiento de políticas');

  const { data: resource, error: resourceError } = await supabase
    .from('gfi_resources')
    .insert({
      title: title.trim(),
      description: description?.trim() ?? '',
      category: category.trim(),
      audience,
      uploaded_by: user.id,
      compliance_confirmed: true,
    })
    .select('id')
    .single();

  if (resourceError) throw resourceError;

  let storagePath = null;
  try {
    if (content.type === 'file') {
      const versionId = crypto.randomUUID();
      storagePath = await uploadResourceFile(resource.id, versionId, content.file);
      content.versionId = versionId;
      content.storagePath = storagePath;
    }

    const versionPayload = buildVersionPayload(content, {
      uploadedBy: user.id,
      note: versionNote,
      versionLabel,
    });

    const { error: versionError } = await supabase
      .from('gfi_resource_versions')
      .insert({ resource_id: resource.id, ...versionPayload });

    if (versionError) throw versionError;
    return resource.id;
  } catch (err) {
    if (storagePath) {
      await supabase.storage.from('gfi-resource-files').remove([storagePath]);
    }
    await supabase.from('gfi_resources').delete().eq('id', resource.id);
    throw err;
  }
}

export async function addResourceVersion({
  resourceId,
  mode,
  content,
  versionNote,
  versionLabel,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  if (mode === 'replace') {
    const { error: deleteError } = await supabase
      .from('gfi_resource_versions')
      .delete()
      .eq('resource_id', resourceId);
    if (deleteError) throw deleteError;
  }

  let storagePath = null;
  try {
    if (content.type === 'file') {
      const versionId = crypto.randomUUID();
      storagePath = await uploadResourceFile(resourceId, versionId, content.file);
      content.versionId = versionId;
      content.storagePath = storagePath;
    }

    const versionPayload = buildVersionPayload(content, {
      uploadedBy: user.id,
      note: versionNote,
      versionLabel,
      isCurrent: true,
    });

    const { error: versionError } = await supabase
      .from('gfi_resource_versions')
      .insert({ resource_id: resourceId, ...versionPayload });

    if (versionError) throw versionError;
  } catch (err) {
    if (storagePath) {
      await supabase.storage.from('gfi-resource-files').remove([storagePath]);
    }
    throw err;
  }
}
